import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../styles/OrderStatus.css";
import "../styles/Orders.css";

const OrderStatus: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const resolvedFrontendStatus = (() => {
    const backendStatus = String(order?.orderStatus || "").toLowerCase();

    switch (backendStatus) {
      case "cancelled":
        return "cancelled";
      case "completed":
        return "completed";
      case "return_refund":
        return "return_refund";
      case "to_receive":
      case "shipped":
        return "to_receive";
      case "to_ship":
      case "paid":
      case "processing":
        return "to_ship";
      case "to_pay":
        return "to_pay";
      case "pending":
      default:
        return order?.paymentMethod !== "Cash on Delivery"
          ? "to_ship"
          : "to_pay";
    }
  })();

  const canCancelOrder =
    resolvedFrontendStatus === "to_pay" || resolvedFrontendStatus === "to_ship";

  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"}/orders/${orderId}`,
        );
        setOrder(response.data?.data || null);
      } catch (error) {
        console.error("fetchOrderDetail error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetail();
    }
  }, [orderId]);

  const formatCurrency = (value: number) =>
    `RM${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (loading) {
    return (
      <section className="order-status-page">
        <div className="order-status-container">
          <div className="order-progress-card">Loading order...</div>
        </div>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="order-status-page">
        <div className="order-status-container">
          <div className="order-progress-card">Order not found.</div>
        </div>
      </section>
    );
  }

  const steps = [
    { key: "placed", label: "Order Placed", active: true },
    {
      key: "pay",
      label: "To Pay",
      active: order.paymentMethod === "Cash on Delivery",
    },
    {
      key: "ship",
      label: "To Ship",
      active:
        order.paymentMethod !== "Cash on Delivery" ||
        order.orderStatus === "to_ship",
    },
    {
      key: "receive",
      label: "To Receive",
      active:
        order.orderStatus === "to_receive" || order.orderStatus === "completed",
    },
    {
      key: "rate",
      label: "To Rate",
      active: order.orderStatus === "completed",
    },
  ];

  return (
    <section className="order-status-page">
      <div className="order-status-container">
        <div className="order-status-header">
          <button
            type="button"
            className="order-status-back"
            onClick={() => navigate("/orders")}
          >
            &lt; BACK
          </button>

          <span className="order-status-current">
            {resolvedFrontendStatus.replace("_", " ").toUpperCase()}
          </span>
        </div>

        <div className="order-progress-card">
          <div className="order-progress-steps">
            {steps.map((step, index) => (
              <div className="order-step" key={step.key}>
                <div
                  className={`order-step-circle ${step.active ? "active" : ""}`}
                >
                  {index + 1}
                </div>
                <div className="order-step-label">{step.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="order-status-action-box">
          <p>
            {order.paymentMethod === "Cash on Delivery"
              ? "Waiting for cash on delivery order processing."
              : "Waiting for payment / shipping confirmation for this order."}
          </p>

          <div className="order-status-action-buttons">
            <button type="button" className="orders-btn orders-btn-muted">
              {order.paymentMethod === "Cash on Delivery"
                ? "To Pay"
                : "Pending"}
            </button>

            <button
              type="button"
              className="orders-btn orders-btn-outline"
              onClick={() => navigate("/support")}
            >
              Contact Us
            </button>

            {canCancelOrder && (
              <button
                type="button"
                className="orders-btn orders-btn-outline"
                onClick={async () => {
                  const confirmed = window.confirm(
                    "Are you sure you want to cancel this order?",
                  );
                  if (!confirmed) return;

                  try {
                    await axios.patch(
                      `${
                        import.meta.env.VITE_LYRAZONE_BASE_URL ||
                        "http://localhost:5000/api"
                      }/orders/${encodeURIComponent(order.orderId)}/cancel`,
                    );

                    navigate("/orders");
                  } catch (error: any) {
                    alert(
                      error?.response?.data?.message ||
                        "Unable to cancel order.",
                    );
                  }
                }}
              >
                Cancel Order
              </button>
            )}
          </div>
        </div>

        <div className="order-delivery-card">
          <h3>Delivery Address</h3>
          <p className="delivery-name">{order.deliveryDetails?.receiverName}</p>
          <p>{order.deliveryDetails?.receiverPhone}</p>
          <p>{order.deliveryDetails?.deliveryAddress}</p>
        </div>

        <div className="order-items-card">
          <div className="orders-card-top">
            <div className="orders-card-left">
              <span className="orders-store-name">LyraZone</span>
            </div>

            <div className="orders-order-id">ORDER ID: {order.orderId}</div>
          </div>

          {order.items?.map((item: any) => {
            const productImage = item.image_path
              ? `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${item.id}/${item.image_path}`
              : "/placeholder-product.png";

            return (
              <div className="orders-product-row" key={item.orderItemId}>
                <div className="orders-product-image-wrap">
                  <img
                    src={productImage}
                    alt={item.productName}
                    className="orders-product-image"
                  />
                </div>

                <div className="orders-product-info">
                  <h4>{item.productName}</h4>
                  <p>Variation: Default</p>
                  <span>x{item.quantity}</span>
                </div>

                <div className="orders-product-price-box">
                  <span className="orders-product-price">
                    {formatCurrency(item.unitPrice)}
                  </span>
                </div>
              </div>
            );
          })}

          <div className="order-totals">
            <div className="order-total-row">
              <span>Merchandise Subtotal</span>
              <strong>{formatCurrency(order.merchandiseSubtotal || 0)}</strong>
            </div>
            <div className="order-total-row">
              <span>Shipping Fee</span>
              <strong>{formatCurrency(order.shippingFee || 0)}</strong>
            </div>
            <div className="order-total-row">
              <span>Shipping Discount Subtotal</span>
              <strong>- {formatCurrency(order.shippingDiscount || 0)}</strong>
            </div>
            <div className="order-total-row">
              <span>Order Total</span>
              <strong>{formatCurrency(order.finalTotal || 0)}</strong>
            </div>
            <div className="order-total-row order-total-final">
              <span>Total</span>
              <strong>{formatCurrency(order.finalTotal || 0)}</strong>
            </div>
            <div className="order-total-row">
              <span>Payment Method</span>
              <strong>{order.paymentMethod}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderStatus;
